import React, { FC, ReactNode, useState, Fragment } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay';
import { FormattedMessage } from 'react-intl';

import { trackEvent } from 'dibs-tracking';
import { Button } from 'dibs-elements/exports/Button';

import Lightbulb from 'dibs-icons/exports/Lightbulb';
import ArrowUp from 'dibs-icons/exports/ArrowUp';
import ArrowDown from 'dibs-icons/exports/ArrowDown';

import { ProgressTask } from './ProgressTask';

import {
    getBackgroundTask,
    getProfileTask,
    getLocationTask,
    getEmailTask,
    getAboutTask,
    getProjectsTask,
    TaskState,
} from '../helpers/progressTasks';

import styles from './ProgressTour.scss';

import { profileStatusMutation } from '../mutations/profileStatus';
import { PROFILE_STATUS_ACTIVE } from '../constants';
import logError from '../../shared/helpers/logError';

import { ProgressTour_tradeFirm as TradeFirmType } from './__generated__/ProgressTour_tradeFirm.graphql';

type Props = {
    isEditMode: boolean;
    tradeFirm: TradeFirmType | null;
    relay: RelayProp;
};

export const ProgressTourComponent: FC<Props> = ({ isEditMode, relay, tradeFirm }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    if (!isEditMode) {
        return null;
    }

    const getTasks = (): TaskState[] => {
        const tasks = [
            getBackgroundTask(tradeFirm),
            getProfileTask(tradeFirm),
            getLocationTask(tradeFirm),
            getEmailTask(tradeFirm),
            getAboutTask(tradeFirm),
            getProjectsTask(tradeFirm),
        ];

        tasks.sort((a, b) => {
            if (a.order < b.order) {
                return -1;
            } else if (a.order > b.order) {
                return 1;
            }
            return 0;
        });
        return tasks;
    };

    const getNextString = (): ReactNode => {
        const incompleteTask = getTasks().find(({ completed }) => !completed);

        if (incompleteTask) {
            return incompleteTask.incompleteMessage;
        }
        return (
            <FormattedMessage
                id="abt.designProfile.header.progress.noTasks"
                defaultMessage="Your profile is complete"
            />
        );
    };

    const handlePublish = async (): Promise<void> => {
        trackEvent({
            category: 'trade features',
            action: 'trade profile',
            label: 'publish profile CTA clicked',
        });

        const firmId = tradeFirm?.serviceId;
        if (firmId) {
            setIsSaving(true);
            try {
                await profileStatusMutation(relay.environment, {
                    serviceId: Number(firmId),
                    status: PROFILE_STATUS_ACTIVE,
                });
            } catch (e) {
                logError(e, `Could not publish profile for firm ${firmId}`);
            }
        } else {
            logError('No firm ID found', 'Could not publish profile');
        }

        setIsSaving(false);
    };

    const renderMainBar = (): ReactNode => {
        const profileStatus = tradeFirm?.profileStatus;
        const tasksLeft = getTasks().filter(({ completed, required }) => !completed && required)
            .length;
        const showSubmit = tasksLeft === 0 && profileStatus === 'PENDING';

        const expandLabel = isExpanded ? (
            <Fragment>
                <div className={styles.expandText} data-tn="hide-all">
                    <FormattedMessage
                        id="abt.designProfile.header.progress.hideAll"
                        defaultMessage="Hide All Requirements"
                    />
                </div>
                <ArrowUp className={styles.expandIcon} />
            </Fragment>
        ) : (
            <Fragment>
                <div className={styles.expandText} data-tn="show-all">
                    <FormattedMessage
                        id="abt.designProfile.header.progress.showAll"
                        defaultMessage="Show All Requirements"
                    />
                </div>
                <ArrowDown className={styles.expandIcon} />
            </Fragment>
        );

        return (
            <div className={styles.statusWrapper} data-tn="progress-tour-main-bar">
                <div className={styles.mainBarContent}>
                    {showSubmit ? <div className={styles.buttonBlock}>&nbsp;</div> : null}
                    <div className={styles.headerTextContent}>
                        <Lightbulb className={styles.lightbulbIcon} />
                        <div className={styles.titleStatusText}>{getNextString()}</div>
                        <div
                            className={styles.expandToggle}
                            onClick={() => setIsExpanded(!isExpanded)}
                            data-tn="toggle-extended-bar"
                        >
                            {expandLabel}
                        </div>
                    </div>
                    {showSubmit ? (
                        <div className={styles.buttonBlock}>
                            <Button
                                dataTn="profile-enable-button"
                                onClick={handlePublish}
                                size="medium"
                                disabled={isSaving}
                            >
                                <FormattedMessage
                                    id="abt.designProfile.header.progress.activate"
                                    defaultMessage="Publish"
                                />
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    const renderExtendedBar = (): ReactNode => {
        return (
            <div className={styles.requirementsWrapper} data-tn="progress-tour-extended-bar">
                <div className={styles.requirementsTitle}>
                    <FormattedMessage
                        id="abt.designProfile.header.progress.requirements"
                        defaultMessage="Profile Requirements"
                    />
                </div>
                <div className={styles.requirementsBody}>
                    {getTasks()
                        .filter(({ required }) => required)
                        .map(task => (
                            <Fragment key={`progress-task-${task.name}`}>
                                <ProgressTask {...task} />
                            </Fragment>
                        ))}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.wrapper} data-tn="progress-tour">
            {renderMainBar()}
            {isExpanded && renderExtendedBar()}
        </div>
    );
};

export default createFragmentContainer(ProgressTourComponent, {
    tradeFirm: graphql`
        fragment ProgressTour_tradeFirm on TradeFirmType {
            serviceId
            profileStatus
            publicProfile {
                about
                splashPhoto
                profilePhoto
                designProjectsCover {
                    status
                }
                address {
                    zipCode
                    country
                }
                email
                portfolios {
                    serviceId
                }
            }
        }
    `,
});
